import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AttachmentPayload,
  ConversationPayload,
  MessageFrame,
  MessageKind,
  ParticipantRole,
  ServerFrame,
  TextPayload,
} from '@kairelo/shared';
import { RealtimeClient, type RealtimeStatus } from './realtime.js';
import {
  DecryptionError,
  decryptBlob,
  decryptPayload,
  encryptBlob,
  encryptPayload,
} from './crypto.js';
import { objects } from './api.js';
import { forgetConversationKey } from './keystore.js';
import { log } from './logging.js';
import type { TranslationAvailability } from './translation.js';

/**
 * The conversation engine, shared by the owner and visitor views.
 *
 * Everything readable exists only here, in memory, in the browser:
 *   - outbound content is encrypted before it is handed to the socket;
 *   - inbound content is ciphertext until this hook decrypts it locally;
 *   - a message that cannot be decrypted is kept and displayed as unreadable,
 *     never dropped and never sent anywhere to be recovered (concept §5).
 */

export interface DecryptedMessage {
  id: string;
  seq: number;
  sender: ParticipantRole;
  kind: MessageKind;
  createdAt: string;
  /**
   * Delivery state, for the sender's own messages.
   *
   * `pending` is a local echo shown the instant something is sent; `sent` means
   * the server acknowledged and stored the ciphertext. There is no `read`: this
   * build has no read receipts, so there is nothing honest to show.
   */
  delivery?: 'pending' | 'sent' | 'failed';
  /** Null when this browser holds no key that can read it. */
  payload: ConversationPayload | null;
  /** Present for attachments, so the view can fetch and decrypt on demand. */
  objectKey?: string;
  objectSize?: number;
  /** Set when decryption failed, so the UI can explain rather than show nothing. */
  unreadable?: boolean;
  /** Local-only send state. */
  pending?: boolean;
}

export interface ConversationState {
  status: RealtimeStatus;
  statusDetail?: string;
  messages: DecryptedMessage[];
  expiresAt: string | null;
  presence: { owner: boolean; visitor: boolean };
  peerTyping: boolean;
  peerTranslation: { active: boolean; availability: TranslationAvailability } | null;
  /**
   * The most recent server-side rejection, for display.
   *
   * A message the server refuses is invisible to the sender unless this is
   * surfaced: the optimistic bubble never appears and nothing explains why.
   */
  lastError: string | null;
  /** True once the conversation has ended (concept §7). */
  ended: boolean;
}

export interface UseConversationOptions {
  conversationId: string;
  role: ParticipantRole;
  /** Visitor session credential. Omitted for the owner (HttpOnly cookie). */
  token?: string;
  /** Null when this browser profile has no key for this conversation. */
  key: CryptoKey | null;
  enabled?: boolean;
}

const MAX_ATTACHMENT_HINT = 'The attachment could not be prepared for sending.';

export function useConversation(options: UseConversationOptions) {
  const { conversationId, role, token, key, enabled = true } = options;

  const [state, setState] = useState<ConversationState>({
    status: 'connecting',
    messages: [],
    expiresAt: null,
    presence: { owner: false, visitor: false },
    peerTyping: false,
    peerTranslation: null,
    lastError: null,
    ended: false,
  });

  const clientRef = useRef<RealtimeClient | null>(null);
  const keyRef = useRef<CryptoKey | null>(key);
  keyRef.current = key;

  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Decrypts an inbound frame. Never throws: unreadable content stays visible as unreadable. */
  const decryptFrame = useCallback(
    async (frame: MessageFrame): Promise<DecryptedMessage> => {
      const base: DecryptedMessage = {
        id: frame.id,
        seq: frame.seq,
        sender: frame.sender,
        kind: frame.kind,
        createdAt: frame.createdAt,
        delivery: 'sent',
        payload: null,
        ...(frame.objectKey ? { objectKey: frame.objectKey } : {}),
        ...(frame.objectSize !== undefined ? { objectSize: frame.objectSize } : {}),
      };

      const currentKey = keyRef.current;
      if (!currentKey) return { ...base, unreadable: true };

      try {
        const payload = await decryptPayload({
          key: currentKey,
          conversationId,
          sender: frame.sender,
          kind: frame.kind,
          envelope: frame.envelope,
        });
        return { ...base, payload };
      } catch (error) {
        if (!(error instanceof DecryptionError)) log.warn('decryption failed unexpectedly');
        return { ...base, unreadable: true };
      }
    },
    [conversationId],
  );

  useEffect(() => {
    if (!enabled) return;

    const client = new RealtimeClient({
      scope: 'conversation',
      ...(token ? { token } : {}),
      conversationId,
      onStatus: (status, detail) =>
        setState((previous) => ({
          ...previous,
          status,
          statusDetail: detail,
          ended: status === 'expired' ? true : previous.ended,
        })),
      onFrame: (frame: ServerFrame) => {
        void (async () => {
          switch (frame.t) {
            case 'ready':
              setState((previous) => ({
                ...previous,
                expiresAt: frame.expiresAt ?? previous.expiresAt,
              }));
              return;

            case 'message': {
              const decrypted = await decryptFrame(frame);
              setState((previous) => {
                // Replace the optimistic local copy if one exists.
                const withoutDuplicate = previous.messages.filter(
                  (message) => message.id !== decrypted.id && message.seq !== decrypted.seq,
                );
                const next = [...withoutDuplicate, decrypted].sort((a, b) => a.seq - b.seq);
                return { ...previous, messages: next };
              });
              return;
            }

            case 'ack':
              setState((previous) => ({
                ...previous,
                messages: previous.messages.map((message) =>
                  message.id === `local-${frame.clientId}`
                    ? { ...message, id: frame.id, seq: frame.seq, delivery: 'sent' as const }
                    : message,
                ),
              }));
              return;

            case 'presence':
              setState((previous) => ({
                ...previous,
                presence: { owner: frame.owner, visitor: frame.visitor },
              }));
              return;

            case 'typing':
              if (frame.sender === role) return;
              setState((previous) => ({ ...previous, peerTyping: frame.active }));
              return;

            case 'translation-state':
              if (frame.sender === role) return;
              setState((previous) => ({
                ...previous,
                peerTranslation: { active: frame.active, availability: frame.availability },
              }));
              return;

            case 'expired':
              setState((previous) => ({ ...previous, ended: true, status: 'expired' }));
              // Clients drop their local key when they observe expiry, on a
              // best-effort basis (concept §7).
              void forgetConversationKey(conversationId);
              return;

            case 'error':
              // Expiry and authentication have their own dedicated states; the
              // rest are shown to the sender so a refused message is never
              // silently dropped.
              log.warn('realtime error frame', { code: frame.code });
              if (frame.code !== 'expired' && frame.code !== 'unauthenticated') {
                setState((previous) => ({ ...previous, lastError: frame.message }));
              }
              return;

            default:
              return;
          }
        })();
      },
    });

    clientRef.current = client;
    client.connect();

    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [conversationId, decryptFrame, enabled, role, token]);

  /** Re-decrypts the backlog when a key arrives after the first render. */
  useEffect(() => {
    if (!key) return;
    setState((previous) => {
      if (!previous.messages.some((message) => message.unreadable)) return previous;
      return previous;
    });
  }, [key]);

  /** Clears a displayed rejection once the sender acts again. */
  const clearError = useCallback(() => {
    setState((previous) =>
      previous.lastError === null ? previous : { ...previous, lastError: null },
    );
  }, []);

  /**
   * Adds the sender's own message immediately, before the server confirms it.
   *
   * Without this, a sent message does not appear until it has been encrypted,
   * relayed, stored and broadcast back — which feels broken on a slow link, and
   * leaves the delivery indicator with nothing to say.
   */
  const echo = useCallback(
    (clientId: string, kind: MessageKind, payload: ConversationPayload) => {
      setState((previous) => ({
        ...previous,
        messages: [
          ...previous.messages,
          {
            id: `local-${clientId}`,
            // Sorts last until the ack supplies the real sequence number.
            seq: Number.MAX_SAFE_INTEGER - (previous.messages.length + 1),
            sender: role,
            kind,
            createdAt: new Date().toISOString(),
            delivery: 'pending' as const,
            payload,
          },
        ],
      }));
    },
    [role],
  );

  /** Marks a local echo as failed, so a lost message is visible rather than gone. */
  const markFailed = useCallback((clientId: string) => {
    setState((previous) => ({
      ...previous,
      messages: previous.messages.map((message) =>
        message.id === `local-${clientId}` ? { ...message, delivery: 'failed' as const } : message,
      ),
    }));
  }, []);

  const sendText = useCallback(
    async (body: string, lang?: string) => {
      const currentKey = keyRef.current;
      if (!currentKey) throw new Error('No conversation key is held in this browser.');
      const payload: TextPayload = { kind: 'text', body, ...(lang ? { lang } : {}) };
      const clientId = crypto.randomUUID();
      echo(clientId, 'text', payload);

      const envelope = await encryptPayload({
        key: currentKey,
        conversationId,
        sender: role,
        payload,
      });
      const sent = clientRef.current?.send({ t: 'send', clientId, kind: 'text', envelope });
      if (!sent) {
        markFailed(clientId);
        throw new Error('Not connected. The message was not sent.');
      }
    },
    [conversationId, echo, markFailed, role],
  );

  /**
   * Encrypts a file locally, uploads the ciphertext straight to object storage
   * with a presigned URL, then sends a message referencing the opaque object
   * key. The plaintext bytes and the filename never leave the browser
   * unencrypted (concept §6).
   */
  const sendAttachment = useCallback(
    async (params: {
      file: Blob;
      filename: string;
      mimeType: string;
      kind: Exclude<MessageKind, 'text'>;
      caption?: string;
      durationSeconds?: number;
      width?: number;
      height?: number;
      onProgress?: (fraction: number) => void;
    }) => {
      const currentKey = keyRef.current;
      if (!currentKey) throw new Error('No conversation key is held in this browser.');

      const plaintext = await params.file.arrayBuffer();
      const { ciphertext, iv } = await encryptBlob({
        key: currentKey,
        conversationId,
        sender: role,
        kind: params.kind,
        data: plaintext,
      });

      const ticket = await objects.requestUpload(conversationId, ciphertext.byteLength);
      params.onProgress?.(0.1);

      const upload = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': ticket.requiredContentType },
        body: ciphertext as BodyInit,
      });
      if (!upload.ok) throw new Error(MAX_ATTACHMENT_HINT);
      params.onProgress?.(0.8);

      const payload: AttachmentPayload & { kind: typeof params.kind } = {
        kind: params.kind,
        filename: params.filename,
        mimeType: params.mimeType,
        size: plaintext.byteLength,
        ...(params.caption ? { caption: params.caption } : {}),
        objectIv: iv,
        ...(params.durationSeconds !== undefined
          ? { durationSeconds: params.durationSeconds }
          : {}),
        ...(params.width !== undefined ? { width: params.width } : {}),
        ...(params.height !== undefined ? { height: params.height } : {}),
      };

      // The object's own nonce travels inside the encrypted message payload, so
      // storage metadata reveals nothing about the content.
      const envelope = await encryptPayload({
        key: currentKey,
        conversationId,
        sender: role,
        payload: payload as ConversationPayload,
      });

      const sent = clientRef.current?.send({
        t: 'send',
        clientId: crypto.randomUUID(),
        kind: params.kind,
        envelope,
        objectKey: ticket.objectKey,
      });
      if (!sent) throw new Error('Not connected. The attachment was not sent.');
      params.onProgress?.(1);
    },
    [conversationId, role],
  );

  /** Fetches and decrypts an attachment on demand. Returns an object URL. */
  const openAttachment = useCallback(
    async (
      message: DecryptedMessage,
    ): Promise<{ url: string; filename: string; mimeType: string }> => {
      const currentKey = keyRef.current;
      if (!currentKey || !message.objectKey || !message.payload) {
        throw new Error('This attachment cannot be opened in this browser.');
      }
      const attachment = message.payload as AttachmentPayload;
      if (!attachment.objectIv) {
        throw new Error('This attachment is missing its encryption details.');
      }

      const ticket = await objects.requestDownload(conversationId, message.objectKey);
      const response = await fetch(ticket.downloadUrl);
      if (!response.ok) throw new Error('The attachment could not be downloaded.');

      const plaintext = await decryptBlob({
        key: currentKey,
        conversationId,
        sender: message.sender,
        kind: message.kind,
        ciphertext: await response.arrayBuffer(),
        iv: attachment.objectIv,
      });

      const blob = new Blob([plaintext as BlobPart], { type: attachment.mimeType });
      return {
        url: URL.createObjectURL(blob),
        filename: attachment.filename,
        mimeType: attachment.mimeType,
      };
    },
    [conversationId],
  );

  const setTyping = useCallback((active: boolean) => {
    clientRef.current?.send({ t: 'typing', active });
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    if (active) {
      typingTimeout.current = setTimeout(() => {
        clientRef.current?.send({ t: 'typing', active: false });
      }, 4000);
    }
  }, []);

  const announceTranslation = useCallback(
    (active: boolean, availability: TranslationAvailability) => {
      clientRef.current?.send({ t: 'translation-state', active, availability });
    },
    [],
  );

  const remainingMs = useMemo(() => {
    if (!state.expiresAt) return null;
    return Math.max(0, new Date(state.expiresAt).getTime() - Date.now());
  }, [state.expiresAt]);

  return {
    ...state,
    remainingMs,
    sendText,
    sendAttachment,
    openAttachment,
    setTyping,
    announceTranslation,
    clearError,
  };
}
